import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Folder, Plus, Trash2, Edit2, Check, X, Users, CheckSquare, Mail } from 'lucide-react';
import { useProject } from '@/lib/ProjectContext';
import { projectsAPI, contactsAPI, tasksAPI, sequencesAPI } from '@/lib/api';
import { useT } from '@/lib/LanguageContext';

const DEFAULT_STAGE_COLORS = ['#9AA3AB', '#4C6B82', '#A62B1F', '#F59E0B', '#EC4899', '#10B981', '#5C6670'];

const toTitleCase = (s) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const parsePipelineStages = (raw) =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id, idx) => ({
      id,
      name: toTitleCase(id),
      color: DEFAULT_STAGE_COLORS[idx % DEFAULT_STAGE_COLORS.length],
      order: idx,
    }));

const stagesToString = (stages = []) =>
  stages
    .map((s) => (typeof s === 'string' ? s : s?.id))
    .filter(Boolean)
    .join(',');

export default function ProjectSettings({ embedded = false }) {
  const t = useT();
  const { projects, refreshProjects, activeProject } = useProject();
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editStages, setEditStages] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#5C6670');
  const [newStages, setNewStages] = useState('');

  // Per-project stats for the overview
  const [stats, setStats] = useState({}); // { [projectId]: { contacts, openTasks, campaigns } }
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (projects.length === 0) return;
    loadStats();
  }, [projects]);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const results = await Promise.allSettled(
        projects.map(async (p) => {
          const [contactsRes, tasksRes, sequencesRes] = await Promise.allSettled([
            contactsAPI.list({ project_id: p._id, limit: 500 }),
            tasksAPI.list({ project_id: p._id, limit: 200 }),
            sequencesAPI.list({ project_id: p._id }),
          ]);

          const contacts = contactsRes.status === 'fulfilled'
            ? (contactsRes.value.data?.contacts?.length ?? contactsRes.value.data?.length ?? 0)
            : 0;

          const allTasks = tasksRes.status === 'fulfilled'
            ? (Array.isArray(tasksRes.value.data) ? tasksRes.value.data : [])
            : [];
          const openTasks = allTasks.filter((t) => t.status !== 'done').length;

          const allSeqs = sequencesRes.status === 'fulfilled'
            ? (Array.isArray(sequencesRes.value.data) ? sequencesRes.value.data : [])
            : [];
          const campaigns = allSeqs.filter((s) => s.is_active).length;

          return { id: p._id, contacts, openTasks, campaigns };
        })
      );

      const newStats = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          newStats[r.value.id] = r.value;
        }
      });
      setStats(newStats);
    } catch (err) {
      console.error('Failed to load project stats', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const startEdit = (project) => {
    setEditing(project._id);
    setEditName(project.name);
    setEditColor(project.color || '#5C6670');
    setEditStages(stagesToString(project.pipeline_stages));
  };

  const saveEdit = async (projectId) => {
    try {
      await projectsAPI.update(projectId, {
        name: editName,
        color: editColor,
        pipeline_stages: parsePipelineStages(editStages),
      });
      await refreshProjects();
      setEditing(null);
      toast.success('Project updated');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? `Failed to update: ${detail}` : 'Failed to update project');
    }
  };

  const createProject = async () => {
    if (!newName.trim()) {
      toast.error('Project name is required');
      return;
    }
    const stages = parsePipelineStages(newStages);
    try {
      await projectsAPI.create({ name: newName, color: newColor, pipeline_stages: stages });
      await refreshProjects();
      setShowCreate(false);
      setNewName('');
      setNewColor('#5C6670');
      setNewStages('');
      toast.success('Project created');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? `Failed to create project: ${detail}`
        : Array.isArray(detail)
        ? `Failed to create project: ${detail.map((d) => d.msg).join(', ')}`
        : 'Failed to create project';
      toast.error(msg);
    }
  };

  const deleteProject = async (projectId) => {
    if (!confirm('Are you sure you want to archive this project? You can reactivate it later.')) return;
    try {
      await projectsAPI.delete(projectId);
      await refreshProjects();
      toast.success('Project archived');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? `Failed to archive: ${detail}` : 'Failed to archive project');
    }
  };

  const Wrap = embedded ? React.Fragment : Layout;
  const wrapProps = embedded ? {} : { title: t('settings.projects_title') };

  return (
    <Wrap {...wrapProps}>
      <div className="space-y-8">

        {/* ── Overview ──────────────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-semibold text-[#1E2A33] mb-4" style={{ fontFamily: 'Manrope' }}>
            All Projects
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => {
              const s = stats[project._id];
              const isActive = activeProject?._id === project._id;
              return (
                <Card
                  key={project._id}
                  className="border border-[#E3DDD1] shadow-sm hover:shadow transition-shadow"
                  data-testid={`project-overview-${project._id}`}
                >
                  <CardContent className="pt-5 pb-4">
                    {/* Header row */}
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${project.color || '#5C6670'}22` }}
                      >
                        <Folder className="w-5 h-5" style={{ color: project.color || '#5C6670' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#1E2A33] truncate" style={{ fontFamily: 'Manrope' }}>
                            {project.name}
                          </p>
                          {isActive && (
                            <Badge variant="secondary" className="text-[10px] flex-shrink-0">Active</Badge>
                          )}
                        </div>
                        <p className="text-xs text-[#5C6670]">
                          {project.pipeline_stages?.length || 0} pipeline stage{project.pipeline_stages?.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-[#FBF8F2] rounded-lg py-2 px-1">
                        <div className="flex justify-center mb-1">
                          <Users className="w-3.5 h-3.5 text-[#4C6B82]" />
                        </div>
                        <p className="text-base font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                          {statsLoading || !s ? '—' : s.contacts}
                        </p>
                        <p className="text-[10px] text-[#5C6670]">Contacts</p>
                      </div>
                      <div className="bg-[#FBF8F2] rounded-lg py-2 px-1">
                        <div className="flex justify-center mb-1">
                          <CheckSquare className="w-3.5 h-3.5 text-[#A62B1F]" />
                        </div>
                        <p className="text-base font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                          {statsLoading || !s ? '—' : s.openTasks}
                        </p>
                        <p className="text-[10px] text-[#5C6670]">Open Tasks</p>
                      </div>
                      <div className="bg-[#FBF8F2] rounded-lg py-2 px-1">
                        <div className="flex justify-center mb-1">
                          <Mail className="w-3.5 h-3.5 text-[#A62B1F]" />
                        </div>
                        <p className="text-base font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                          {statsLoading || !s ? '—' : s.campaigns}
                        </p>
                        <p className="text-[10px] text-[#5C6670]">Campaigns</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* "New Project" quick-add tile */}
            {!showCreate && (
              <button
                onClick={() => setShowCreate(true)}
                className="border-2 border-dashed border-[#D3CCBE] rounded-xl flex flex-col items-center justify-center gap-2 py-8 text-[#9AA3AB] hover:border-[#A62B1F] hover:text-[#A62B1F] transition-colors"
                data-testid="create-project-tile"
              >
                <Plus className="w-6 h-6" />
                <span className="text-sm font-medium">{t('settings.new_project')}</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Create Form ───────────────────────────────────────── */}
        {showCreate && (
          <Card data-testid="create-project-form">
            <CardHeader>
              <CardTitle>{t('settings.new_project')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="new-name">{t('settings.project_name')}</Label>
                <Input
                  id="new-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., Q1 Sales Campaign"
                />
              </div>
              <div>
                <Label htmlFor="new-color">{t('settings.project_color')}</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="new-color"
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    className="w-20 h-10"
                  />
                  <span className="text-sm text-muted-foreground">{newColor}</span>
                </div>
              </div>
              <div>
                <Label htmlFor="new-stages">Pipeline Stages (optional, comma-separated)</Label>
                <Input
                  id="new-stages"
                  value={newStages}
                  onChange={(e) => setNewStages(e.target.value)}
                  placeholder="new_lead,contacted,engaged,..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank if this project doesn&apos;t use the pipeline board.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={createProject}
                  className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
                  data-testid="create-project-button"
                >
                  {t('settings.create_project')}
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>{t('common.cancel')}</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Edit / Manage ─────────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-semibold text-[#1E2A33] mb-4" style={{ fontFamily: 'Manrope' }}>
            Manage Projects
          </h2>
          <div className="space-y-3">
            {projects.map((project) => (
              <Card key={project._id} data-testid={`project-card-${project._id}`}>
                <CardHeader className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: project.color || '#5C6670' }}
                      />
                      {editing === project._id ? (
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-64"
                        />
                      ) : (
                        <div>
                          <CardTitle className="flex items-center gap-2 text-base">
                            {project.name}
                            {activeProject?._id === project._id && (
                              <Badge variant="secondary">Active</Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {project.pipeline_stages?.length || 0} pipeline stages
                          </CardDescription>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {editing === project._id ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => saveEdit(project._id)}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(project)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteProject(project._id)}
                            disabled={projects.length === 1}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {editing === project._id && (
                  <CardContent className="space-y-4 pt-0">
                    <div>
                      <Label>Color</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="color"
                          value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          className="w-20 h-10"
                        />
                        <span className="text-sm text-muted-foreground">{editColor}</span>
                      </div>
                    </div>
                    <div>
                      <Label>Pipeline Stages</Label>
                      <Input
                        value={editStages}
                        onChange={(e) => setEditStages(e.target.value)}
                        placeholder="new_lead,contacted,engaged,..."
                      />
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Wrap>
  );
}
